import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromUED from './actions';
import {
    mergeMap,
    map,
    catchError,
    flatMap,
    tap,
    withLatestFrom,
    switchMap
} from 'rxjs/operators';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromErrors from '../../shared';
import { Store, select } from '@ngrx/store';
import {
    IStatusMessage,
    ResponseStatus,
    DisplayMessageMinimum
} from '../../shared';
import * as fromShared from '../../shared';
import { LoadGroups } from './actions';
import { UserEntryDatesService } from '../userentrydates.service';

@Injectable()
export class UserEntryDatesEffects {
    constructor(
        private actions$: Actions,
        private service: UserEntryDatesService,
        private log: LoggingService
    ) {}

    LoadEntryDatesByGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUED.LoadEntryDatesByGroup>(
                fromUED.ActionTypes.LoadEntryDatesByGroup
            ),
            tap(action =>
                this.log.debug({
                    type: 'UserEntryDatesEffects:LoadEntryDatesByGroup$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getEntryDatesByGroup(x.payload).pipe(
                    map(exp => new fromUED.SetEntryDatesByGroup(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUED.LoadGroups>(fromUED.ActionTypes.LoadGroups),
            tap(action =>
                this.log.debug({
                    type: 'UserEntryDatesEffects:LoadGroups$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getUserGroups().pipe(
                    map(exp => new fromUED.SetGroups(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadUserEntryDates$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUED.LoadUserEntryDates>(
                fromUED.ActionTypes.LoadUserEntryDates
            ),
            tap(action =>
                this.log.debug({
                    type: 'UserEntryDatesEffects:LoadUserEntryDates$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getEntryDates().pipe(
                    map(exp => new fromUED.SetUserEntryDates(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    UpdateUserEntryDates$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUED.UpdateUserEntryDates>(
                fromUED.ActionTypes.UpdateUserEntryDates
            ),
            tap(x =>
                this.log.verbose({
                    type: 'UserEntryDatesEffects:UpdateUserEntryDates$',
                    x
                })
            ),
            switchMap(a =>
                this.service.putEntryDates([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromUED.UpdateUserEntryDatesSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.EmployeeListId.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
